import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, X, Plus, Save, Info } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Wine, WineType } from '@/types/wine';

const wineTypeLabels: Record<WineType, string> = {
  red: 'Rosso',
  white: 'Bianco',
  rose: 'Rosato',
  sparkling: 'Spumante',
  dessert: 'Dessert',
  fortified: 'Fortificato',
  orange: 'Arancione',
  whisky: 'Whisky',
  rum: 'Rum',
  gin: 'Gin',
  grappa: 'Grappa',
  cognac: 'Cognac',
  liqueur: 'Liquore',
  other: 'Altro',
};

interface DuplicateModalProps {
  isOpen: boolean;
  onClose: () => void;
  duplicateWine: Wine;
  onIncrement: () => void;
  onInsertAnyway: () => void;
  onIgnore: () => void;
  incrementing: boolean;
}

export const DuplicateModal: React.FC<DuplicateModalProps> = ({
  isOpen,
  onClose,
  duplicateWine,
  onIncrement,
  onInsertAnyway,
  onIgnore,
  incrementing,
}) => {
  if (!isOpen) return null;

  const wineImage = duplicateWine.imageUrls?.[0] || duplicateWine.imageUrl;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-yellow-50 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <AlertCircle size={32} className="text-yellow-600" />
              <h2 className="text-2xl font-bold text-gray-900">Vino Duplicato Trovato</h2>
            </div>
            <button
              onClick={onClose}
              disabled={incrementing}
              className="p-2 hover:bg-yellow-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <X size={24} className="text-gray-700" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Info message */}
            <div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Questo vino sembra essere già presente in cantina</p>
                <p className="text-blue-700">
                  Puoi scegliere di incrementare la quantità del vino esistente (+1 bottiglia)
                  oppure inserirlo comunque come nuovo vino separato.
                </p>
              </div>
            </div>

            {/* Wine Details Card */}
            <Card className="overflow-hidden">
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Vino già presente:</h3>

                <div className="flex gap-4">
                  {/* Wine Image */}
                  {wineImage ? (
                    <img
                      src={wineImage}
                      alt={duplicateWine.name}
                      className="w-24 h-24 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-gradient-to-br from-wine-burgundy/10 to-wine-purple/10 rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-4xl">🍷</span>
                    </div>
                  )}

                  {/* Wine Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xl font-bold text-gray-900 mb-1 truncate">
                      {duplicateWine.name}
                    </h4>
                    <p className="text-base text-gray-700 mb-2 truncate">
                      {duplicateWine.producer}
                    </p>

                    <div className="flex flex-wrap gap-2 text-sm">
                      <span className="px-2 py-1 bg-wine-burgundy/10 text-wine-burgundy rounded-md font-medium">
                        {wineTypeLabels[duplicateWine.type]}
                      </span>

                      {duplicateWine.vintage && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md font-medium">
                          Annata {duplicateWine.vintage}
                        </span>
                      )}

                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-md font-medium">
                        Quantità attuale: {duplicateWine.quantity || 1} {(duplicateWine.quantity || 1) === 1 ? 'bottiglia' : 'bottiglie'}
                      </span>
                    </div>

                    {duplicateWine.denomination && (
                      <p className="text-sm text-gray-600 mt-2">
                        {duplicateWine.denomination}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Image Info Warning */}
            <div className="flex gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <Info size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                <span className="font-medium">Nota:</span> Se incrementi la quantità,
                le eventuali nuove immagini caricate verranno ignorate e verrà mantenuta
                l'immagine del vino esistente.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-2">
              {/* Primary action - Increment */}
              <Button
                variant="primary"
                icon={<Plus size={20} />}
                onClick={onIncrement}
                loading={incrementing}
                disabled={incrementing}
                fullWidth
                className="justify-center text-base py-3"
              >
                Incrementa Quantità (+1 bottiglia)
              </Button>

              {/* Secondary actions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  icon={<Save size={20} />}
                  onClick={onInsertAnyway}
                  disabled={incrementing}
                  fullWidth
                  className="justify-center"
                >
                  Inserisci Come Nuovo
                </Button>

                <Button
                  variant="ghost"
                  icon={<X size={20} />}
                  onClick={onIgnore}
                  disabled={incrementing}
                  fullWidth
                  className="justify-center"
                >
                  Ignora
                </Button>
              </div>

              {/* Cancel button */}
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={incrementing}
                fullWidth
                className="justify-center text-gray-600"
              >
                Annulla
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
